import type { NoteItem } from '@personal-os/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { CommandApiError, describeCommandError, undoChange } from '../core/commandClient';
import { useCommandRunner, type RunnerStatus } from '../core/useCommandRunner';
import { fetchNotes, NOTES_KEY } from './api';

const HISTORY_KEY = ['history'];

function StatusLine({ status }: { status: RunnerStatus }) {
  if (status === 'idle') return null;
  return (
    <p role="status" className="status-text" data-testid="runner-status" data-state={status}>
      {status === 'waiting'
        ? "Waiting to retry… you seem to be offline. It will save by itself when you're back, and only once."
        : 'Saving…'}
    </p>
  );
}

interface Undoable {
  commandId: string;
  text: string;
}

export function ScratchPage() {
  const client = useQueryClient();
  const notes = useQuery({ queryKey: NOTES_KEY, queryFn: fetchNotes });
  const { status, run } = useCommandRunner();
  const titleId = useId();
  const bodyId = useId();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [undo, setUndo] = useState<Undoable | null>(null);

  // The "Undo" notice goes away by itself after a little while.
  useEffect(() => {
    if (!undo) return;
    const timer = window.setTimeout(() => setUndo(null), 15_000);
    return () => window.clearTimeout(timer);
  }, [undo]);

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: NOTES_KEY }),
      client.invalidateQueries({ queryKey: HISTORY_KEY }),
    ]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    try {
      await run('note.create', { title, body });
      setTitle('');
      setBody('');
      await refresh();
    } catch (err) {
      setMessage(describeCommandError(err));
    }
  }

  async function undoLast() {
    if (!undo) return;
    try {
      await undoChange(undo.commandId);
      setUndo(null);
      await refresh();
    } catch (err) {
      setMessage(describeCommandError(err));
      setUndo(null);
    }
  }

  return (
    <main className="page">
      <p className="back">
        <Link to="/">← Home</Link>
      </p>
      <h1>Scratch notes</h1>
      <p className="version">
        A temporary page for trying out changes, History and undo. Removed later.
      </p>

      <div className="stack">
        <section className="card" aria-labelledby="add-title">
          <h2 id="add-title" className="card-heading">
            Add a note
          </h2>
          <form onSubmit={(e) => void add(e)} className="stack">
            <label htmlFor={titleId}>Title</label>
            <input
              id={titleId}
              type="text"
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <label htmlFor={bodyId}>Note</label>
            <textarea
              id={bodyId}
              rows={3}
              maxLength={5000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <button
              type="submit"
              className="primary"
              disabled={status !== 'idle' || title.trim() === ''}
            >
              Save
            </button>
            <StatusLine status={status} />
          </form>
        </section>

        {message && (
          <p role="alert" className="error-text">
            {message}
          </p>
        )}

        {undo && (
          <div className="toast" role="status" data-testid="undo-toast">
            <span>{undo.text}</span>
            <button type="button" className="primary" onClick={() => void undoLast()}>
              Undo
            </button>
          </div>
        )}

        <section className="card" aria-labelledby="list-title" data-testid="notes">
          <h2 id="list-title" className="card-heading">
            Your notes
          </h2>
          {notes.isPending && <p role="status">Loading…</p>}
          {notes.isError && (
            <p role="alert" className="error-text">
              Couldn't load your notes.{' '}
              <button type="button" onClick={() => void notes.refetch()}>
                Try again
              </button>
            </p>
          )}
          {notes.data && notes.data.length === 0 && (
            <p className="empty">No notes yet. Add one above.</p>
          )}
          {notes.data && notes.data.length > 0 && (
            <ul className="rows">
              {notes.data.map((note) => (
                <NoteRow
                  key={note.id}
                  note={note}
                  onChanged={refresh}
                  onDeleted={(commandId) =>
                    setUndo({ commandId, text: `Deleted "${note.title}".` })
                  }
                />
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}

function NoteRow({
  note,
  onChanged,
  onDeleted,
}: {
  note: NoteItem;
  onChanged: () => Promise<unknown>;
  onDeleted: (commandId: string) => void;
}) {
  const { status, run } = useCommandRunner();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  // The version we based our edit on. Normally the note's own; after a conflict, we choose.
  const [baseVersion, setBaseVersion] = useState(note.version);
  const [conflict, setConflict] = useState<NoteItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function startEditing() {
    setTitle(note.title);
    setBody(note.body);
    setBaseVersion(note.version);
    setConflict(null);
    setMessage(null);
    setEditing(true);
  }

  async function save(e?: FormEvent, version = baseVersion) {
    e?.preventDefault();
    setMessage(null);
    try {
      await run('note.update', { id: note.id, expectedVersion: version, title, body });
      setEditing(false);
      setConflict(null);
      await onChanged();
    } catch (err) {
      if (err instanceof CommandApiError && err.code === 'version_conflict') {
        setConflict(err.details.current as NoteItem);
        return;
      }
      setMessage(describeCommandError(err));
    }
  }

  async function remove() {
    setMessage(null);
    try {
      const outcome = await run('note.delete', { id: note.id, expectedVersion: note.version });
      onDeleted(outcome.commandId);
      await onChanged();
    } catch (err) {
      setMessage(describeCommandError(err));
    }
  }

  if (editing) {
    return (
      <li data-testid="note-row" data-editing="true">
        <form onSubmit={(e) => void save(e)} className="stack note-edit">
          <label htmlFor={`t-${note.id}`}>Title</label>
          <input
            id={`t-${note.id}`}
            type="text"
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <label htmlFor={`b-${note.id}`}>Note</label>
          <textarea
            id={`b-${note.id}`}
            rows={3}
            maxLength={5000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="button-row">
            <button
              type="submit"
              className="primary"
              disabled={status !== 'idle' || title.trim() === ''}
            >
              Save changes
            </button>
            <button type="button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          <StatusLine status={status} />
          {conflict && (
            <div className="conflict" role="alert" data-testid="conflict">
              <p>
                <strong>This changed since you opened it.</strong> It was saved somewhere else
                (another tab or device) after you started editing. Nothing was overwritten.
              </p>
              <p className="hint">Their version</p>
              <p className="conflict-text">
                <strong>{conflict.title}</strong>
                {conflict.body ? ` · ${conflict.body}` : ''}
              </p>
              <div className="button-row">
                <button
                  type="button"
                  onClick={() => {
                    setTitle(conflict.title);
                    setBody(conflict.body);
                    setBaseVersion(conflict.version);
                    setConflict(null);
                  }}
                >
                  Use their version
                </button>
                <button
                  type="button"
                  className="primary"
                  onClick={() => {
                    setBaseVersion(conflict.version);
                    void save(undefined, conflict.version);
                  }}
                >
                  Save mine over theirs
                </button>
              </div>
            </div>
          )}
          {message && (
            <p role="alert" className="error-text">
              {message}
            </p>
          )}
        </form>
      </li>
    );
  }

  return (
    <li data-testid="note-row">
      <div>
        <strong>{note.title}</strong>
        {note.body && <p className="note-body">{note.body}</p>}
        {note.processedAt && <p className="hint">Processed by the worker ✓</p>}
        {message && (
          <p role="alert" className="error-text">
            {message}
          </p>
        )}
        <StatusLine status={status} />
      </div>
      <div className="button-row">
        <button type="button" onClick={startEditing}>
          Edit
        </button>
        <button type="button" disabled={status !== 'idle'} onClick={() => void remove()}>
          Delete
        </button>
      </div>
    </li>
  );
}
